import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Trash2, PencilRuler } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/Badges';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, fmtMoney, fmtDate } from '../lib/format';

export default function ContenedoresPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { canDelete } = useAuth();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ nombre: '', client_id: '', moneda: 'MXN', iva_percentage: 16 });

  const { data: cotizaciones, isLoading } = useQuery({
    queryKey: ['container-quotations', search],
    queryFn: async () => (await api.get('/container-quotations', { params: search ? { search } : {} })).data,
  });

  const { data: clientes } = useQuery({ queryKey: ['clients', 'todos', ''], queryFn: async () => (await api.get('/clients')).data });

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/container-quotations', { ...form, iva_percentage: Number(form.iva_percentage), client_id: form.client_id || null })).data,
    onSuccess: (q) => {
      queryClient.invalidateQueries({ queryKey: ['container-quotations'] });
      toast.success(`Cotización ${q.quotation_number} creada`);
      setDialogOpen(false);
      navigate(`/contenedores/${q.id}`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/container-quotations/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['container-quotations'] });
      toast.success('Cotización eliminada');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Cotizaciones de Contenedores</h2>
          <p className="text-sm text-muted-foreground">Cotización simplificada: líneas con precio y descuento</p>
        </div>
        <Button onClick={() => { setForm({ nombre: '', client_id: '', moneda: 'MXN', iva_percentage: 16 }); setDialogOpen(true); }} data-testid="new-container-quotation-button">
          <Plus size={16} className="mr-1.5" /> Nueva cotización
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="pl-9" data-testid="container-quotations-search-input" />
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="container-quotations-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Número</TableHead>
                  <TableHead className="th-label">Nombre</TableHead>
                  <TableHead className="th-label">Cliente</TableHead>
                  <TableHead className="th-label">Estado</TableHead>
                  <TableHead className="th-label text-right">Total</TableHead>
                  <TableHead className="th-label">Actualizada</TableHead>
                  <TableHead className="th-label w-14"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(cotizaciones || []).length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin cotizaciones de contenedores</TableCell></TableRow>
                )}
                {(cotizaciones || []).map((q) => (
                  <TableRow key={q.id} className="hover:bg-background/40 cursor-pointer" data-testid={`container-quotation-row-${q.quotation_number}`} onClick={() => navigate(`/contenedores/${q.id}`)}>
                    <TableCell className="font-mono text-[13px]">{q.quotation_number}</TableCell>
                    <TableCell className="max-w-[280px] truncate font-medium">{q.nombre}</TableCell>
                    <TableCell className="text-muted-foreground">{q.cliente_nombre}</TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                    <TableCell className="money">{fmtMoney(q.totals?.total, q.moneda)}</TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">{fmtDate(q.updated_at)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`container-actions-${q.quotation_number}`}>
                            <MoreHorizontal size={15} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/contenedores/${q.id}`)}>
                            <PencilRuler size={14} className="mr-2" /> Abrir
                          </DropdownMenuItem>
                          {canDelete && (
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => deleteMutation.mutate(q.id)}>
                              <Trash2 size={14} className="mr-2" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-container-quotation-dialog">
          <DialogHeader><DialogTitle>Nueva cotización de contenedores</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} data-testid="container-quotation-nombre-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                <SelectTrigger data-testid="container-quotation-client-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>
                  {(clientes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Moneda</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm((f) => ({ ...f, moneda: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>IVA %</Label>
                <Input type="number" value={form.iva_percentage} onChange={(e) => setForm((f) => ({ ...f, iva_percentage: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!form.nombre || createMutation.isPending} data-testid="create-container-quotation-button">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
